import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Popper,
  Paper,
  MenuList,
  MenuItem,
  Chip,
  Typography,
  IconButton,
  InputAdornment,
  FormControl,
  FormLabel,
  FormHelperText,
  CircularProgress,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Add as AddIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import HtmlDescription from './HtmlDescription';

const SearchableSelect = ({
  label,
  placeholder = "Search and select...",
  value,
  onChange,
  options = [],
  multiple = false,
  allowCustomInput = false,
  customInputLabel = "Add custom option",
  loading = false,
  disabled = false,
  required = false,
  error = false,
  helperText,
  description,
  size = "medium",
  variant = "outlined",
  fullWidth = true,
  searchPlaceholder = "Type to search...",
  noOptionsText = "No options found",
  customInputPlaceholder = "Enter custom value",
  maxHeight = 300,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const searchInputRef = useRef(null);

  // Handle multiple vs single value
  const currentValue = multiple ? (value || []) : value;
  const hasValue = multiple ? (currentValue && currentValue.length > 0) : (currentValue !== null && currentValue !== undefined && currentValue !== '');

  // Filter options based on search
  const filteredOptions = options.filter(option => {
    const optionText = typeof option === 'string' ? option : (option.label || option.value || option);
    return optionText.toLowerCase().includes(searchValue.toLowerCase());
  });

  // Handle option selection
  const handleOptionSelect = (option) => {
    const optionValue = typeof option === 'string' ? option : (option.value || option.label || option);
    const optionLabel = typeof option === 'string' ? option : (option.label || option.value || option);

    if (multiple) {
      const newValue = [...currentValue];
      const existingIndex = newValue.findIndex(item => 
        (typeof item === 'string' ? item : item.value) === optionValue
      );
      
      if (existingIndex >= 0) {
        newValue.splice(existingIndex, 1);
      } else {
        newValue.push({ value: optionValue, label: optionLabel });
      }
      onChange(newValue);
    } else {
      onChange({ value: optionValue, label: optionLabel });
      setOpen(false);
      setSearchValue('');
    }
  };

  // Handle custom input
  const handleCustomInputSubmit = () => {
    if (customInput.trim()) {
      if (multiple) {
        const newValue = [...currentValue, { value: customInput.trim(), label: customInput.trim(), isCustom: true }];
        onChange(newValue);
      } else {
        onChange({ value: customInput.trim(), label: customInput.trim(), isCustom: true });
        setOpen(false);
      }
      setCustomInput('');
      setShowCustomInput(false);
    }
  };

  // Handle remove chip (for multiple selection)
  const handleRemoveChip = (valueToRemove) => {
    if (multiple) {
      const newValue = currentValue.filter(item => 
        (typeof item === 'string' ? item : item.value) !== valueToRemove
      );
      onChange(newValue);
    }
  };

  // Handle input click to open dropdown
  const handleInputClick = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setOpen(true);
      setSearchValue('');
      setShowCustomInput(false);
      // Focus search input after a short delay
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  };

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (anchorEl && !anchorEl.contains(event.target)) {
        setOpen(false);
        setSearchValue('');
        setShowCustomInput(false);
        setCustomInput('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorEl]);

  // Get display value
  const getDisplayValue = () => {
    if (multiple) {
      return currentValue.map(item => 
        typeof item === 'string' ? item : item.label
      ).join(', ');
    } else {
      return currentValue && currentValue.label ? currentValue.label : '';
    }
  };

  // Check if option is selected
  const isOptionSelected = (option) => {
    const optionValue = typeof option === 'string' ? option : (option.value || option.label || option);
    
    if (multiple) {
      return currentValue.some(item => 
        (typeof item === 'string' ? item : item.value) === optionValue
      );
    } else {
      return currentValue && currentValue.value === optionValue;
    }
  };

  return (
    <FormControl fullWidth={fullWidth} error={error} required={required}>
      <TextField
        {...props}
        ref={inputRef}
        fullWidth={fullWidth}
        size={size}
        variant={variant}
        label={label ? (
          required ? (
            <span>
              <span style={{ color: '#f44336' }}>*</span> {label}
            </span>
          ) : label
        ) : undefined}
        value={getDisplayValue()}
        placeholder={placeholder}
        disabled={disabled}
        onClick={handleInputClick}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {loading ? (
                <CircularProgress size={20} />
              ) : (
                <ArrowDownIcon 
                  sx={{ 
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }} 
                />
              )}
            </InputAdornment>
          ),
        }}
      />

      {/* Selected chips for multiple selection */}
      {multiple && hasValue && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {currentValue.map((item, index) => {
            const itemValue = typeof item === 'string' ? item : item.value;
            const itemLabel = typeof item === 'string' ? item : item.label;
            return (
              <Chip
                key={index}
                label={itemLabel}
                onDelete={disabled ? undefined : () => handleRemoveChip(itemValue)}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
                variant="outlined"
              />
            );
          })}
        </Box>
      )}

      {/* Dropdown Popper */}
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1300, width: anchorEl ? anchorEl.offsetWidth : 'auto' }}
      >
        <Paper 
          sx={{ 
            maxHeight: maxHeight, 
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          {/* Search Input */}
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              ref={searchInputRef}
              fullWidth
              size="small"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Options List */}
          <Box sx={{ maxHeight: maxHeight - 100, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : filteredOptions.length > 0 ? (
              <MenuList>
                {filteredOptions.map((option, index) => {
                  const optionValue = typeof option === 'string' ? option : (option.value || option.label || option);
                  const optionLabel = typeof option === 'string' ? option : (option.label || option.value || option);
                  const selected = isOptionSelected(option);
                  
                  return (
                    <MenuItem
                      key={index}
                      onClick={() => handleOptionSelect(option)}
                      selected={selected}
                      sx={{
                        backgroundColor: selected ? 'primary.main' : 'transparent',
                        color: selected ? 'primary.contrastText' : 'text.primary',
                        '&:hover': {
                          backgroundColor: selected ? 'primary.dark' : 'action.hover',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {optionLabel}
                        </Typography>
                        {multiple && selected && (
                          <CloseIcon fontSize="small" />
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </MenuList>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {noOptionsText}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Custom Input Section */}
          {allowCustomInput && (
            <>
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 1 }}>
                {!showCustomInput ? (
                  <Button
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={() => setShowCustomInput(true)}
                    size="small"
                    variant="text"
                  >
                    {customInputLabel}
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={customInputPlaceholder}
                      value={customInput}
                      onChange={(e) => setCustomInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleCustomInputSubmit();
                        }
                      }}
                      autoFocus
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleCustomInputSubmit}
                      disabled={!customInput.trim()}
                    >
                      Add
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setShowCustomInput(false);
                        setCustomInput('');
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Popper>

      {/* Helper Text and Description */}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {description && <HtmlDescription description={description} />}
    </FormControl>
  );
};

export default SearchableSelect;
